import type {
  ConnectivityCheckResponse,
  Machine,
  MachineConnectivityStatus,
  MachineFormValues,
  MachineTableRow,
  MachineUpsertPayload,
  PacsMachineApi,
} from '../types/machine.types';
import { formatRelativeTimeAgo } from './dateFormatter';

function normalizeStatusToken(status?: string | null): string {
  return String(status ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

export function parseConnectivityStatus(raw: PacsMachineApi): MachineConnectivityStatus {
  if (raw.online === true) {
    return 'Online';
  }
  if (raw.online === false) {
    return 'Offline';
  }

  const status = raw.connectivityStatus ?? raw.connectivity_status;
  if (!status) {
    return 'Unknown';
  }

  return parseConnectivityCheckStatus(String(status));
}

export function parseConnectivityCheckStatus(status?: string): MachineConnectivityStatus {
  const normalized = normalizeStatusToken(status);
  if (!normalized) {
    return 'Unknown';
  }

  if (
    normalized === 'ONLINE' ||
    normalized === 'ON' ||
    normalized === 'REACHABLE' ||
    normalized === 'OK'
  ) {
    return 'Online';
  }

  if (
    normalized === 'CONFIGURATION_ERROR' ||
    normalized === 'CONFIG_ERROR' ||
    normalized === 'CONFIGURATIONERROR' ||
    normalized === 'MISCONFIGURED' ||
    normalized === 'INVALID_CONFIGURATION'
  ) {
    return 'ConfigurationError';
  }

  if (
    normalized === 'OFFLINE' ||
    normalized === 'OFF' ||
    normalized === 'UNREACHABLE' ||
    normalized === 'DOWN'
  ) {
    return 'Offline';
  }

  if (normalized === 'CHECKING' || normalized === 'PENDING' || normalized === 'SCHEDULED') {
    return 'Checking';
  }

  return 'Unknown';
}

/**
 * Apply live connectivity check result (status + lastChecked from check API).
 */
export function applyConnectivityCheckToMachine(
  existing: Machine,
  check: ConnectivityCheckResponse
): Machine {
  return {
    ...existing,
    connectivityStatus: parseConnectivityCheckStatus(check.status),
    lastCheckedAt: check.lastChecked || existing.lastCheckedAt,
    responseTime: check.responseTime ?? null,
    failureReason: check.failureReason ?? check.message ?? null,
  };
}

export function parseLastCheckedAt(raw: PacsMachineApi): string | null {
  const value =
    raw.lastStudyReceivedAt ??
    raw.lastStudyAt ??
    raw.last_study_at ??
    raw.lastStudy ??
    raw.last_study ??
    raw.lastChecked ??
    raw.lastCheckedAt ??
    raw.last_checked ??
    raw.last_checked_at ??
    raw.lastCheckTime;

  if (!value) {
    return null;
  }

  return String(value);
}

export const DEFAULT_MACHINE_PORT = '104';

export function mapApiMachineToMachine(raw: PacsMachineApi): Machine {
  return {
    id: raw.pk,
    machineName: raw.machineName ?? '',
    aeTitle: raw.aeTitle ?? '',
    modality: raw.modality ?? '',
    location: raw.location ?? '',
    ipAddress: raw.ipAddress ?? '',
    port: raw.port ?? 104,
    active: raw.active !== false,
    connectivityStatus: parseConnectivityStatus(raw),
    lastCheckedAt: parseLastCheckedAt(raw),
    responseTime: null,
    failureReason: null,
  };
}

export function mapApiMachinesToMachines(raw: PacsMachineApi[] | undefined | null): Machine[] {
  if (!raw || !Array.isArray(raw)) {
    return [];
  }
  return raw.map(mapApiMachineToMachine);
}

export function mapMachineToTableRow(machine: Machine): MachineTableRow {
  return {
    id: machine.id,
    machineName: machine.machineName,
    modality: machine.modality,
    aeTitle: machine.aeTitle,
    ipPort: `${machine.ipAddress}:${machine.port}`,
    location: machine.location,
    activeInactive: machine.active ? 'Active' : 'Inactive',
    active: machine.active,
    connectivityStatus: machine.connectivityStatus,
    lastChecked: machine.lastCheckedAt ? formatRelativeTimeAgo(machine.lastCheckedAt) : '—',
    responseTime: machine.responseTime,
    failureReason: machine.failureReason,
  };
}

export function mapMachineToFormValues(machine: Machine): MachineFormValues {
  return {
    machineName: machine.machineName,
    modalityType: machine.modality,
    location: machine.location,
    aeTitle: machine.aeTitle,
    ipAddress: machine.ipAddress,
    port: machine.port != null ? String(machine.port) : DEFAULT_MACHINE_PORT,
    active: machine.active,
  };
}

export function getDefaultMachineFormValues(): MachineFormValues {
  return {
    machineName: '',
    modalityType: 'CT',
    location: '',
    aeTitle: '',
    ipAddress: '',
    port: DEFAULT_MACHINE_PORT,
    active: true,
  };
}

export function mapFormValuesToUpsertPayload(values: MachineFormValues): MachineUpsertPayload {
  return {
    machineName: values.machineName.trim(),
    aeTitle: values.aeTitle.trim(),
    modality: values.modalityType.trim(),
    location: values.location.trim(),
    ipAddress: values.ipAddress.trim(),
    port: parseInt(values.port, 10),
  };
}
